// player/playerPrefs.ts — the viewer's player choices (1.27.50): speed, quality, fit, look,
// controls. Same shape as useVideoTheater.ts: a tiny Zustand store with its own
// localStorage key and try/catch around storage — a per-browser convenience, never an
// account setting (Autoplay next IS an account setting and lives on the user).
//
// Deliberately NOT here: audio processing ("volume levelling", mono). Web Audio can only
// touch a video's sound when its files are served with CORS headers; ours come through
// Cloudflare's cache without them, and asking for CORS would stop playback outright.

import { create } from 'zustand';

const KEY = 'truxsocial:videoPlayer';

export type Quality = 'auto' | 'hd' | 'standard';
export type VideoFit = 'fit' | 'fill';

export interface PlayerPrefs {
  volume: number;
  muted: boolean;
  speed: number;
  preservePitch: boolean;
  quality: Quality;
  fit: VideoFit;
  /** 0.5 – 1.5, 1 = unchanged. */
  brightness: number;
  contrast: number;
  /** A soft glow of the video's own colours around the player. */
  ambient: boolean;
  alwaysShowControls: boolean;
  largeControls: boolean;
  /** 1.27.60 captions: on/off, the language you picked last, and how they look. */
  captionsOn: boolean;
  captionLang: string;
  captionSize: CaptionSize;
  captionBackground: boolean;
}

export type CaptionSize = 'small' | 'medium' | 'large';

export const DEFAULT_PREFS: PlayerPrefs = {
  volume: 1,
  muted: false,
  speed: 1,
  preservePitch: true,
  quality: 'auto',
  fit: 'fit',
  brightness: 1,
  contrast: 1,
  ambient: true,
  alwaysShowControls: false,
  largeControls: false,
  captionsOn: false,
  captionLang: '',
  captionSize: 'medium',
  captionBackground: true,
};

export const SPEED_PRESETS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
export const SPEED_MIN = 0.25;
export const SPEED_MAX = 3;

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Anything read back from storage is re-validated — a hand-edited value can't break play. */
export function sanitize(raw: unknown): PlayerPrefs {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<
    Record<keyof PlayerPrefs, unknown>
  >;
  const num = (v: unknown, d: number, lo: number, hi: number) =>
    typeof v === 'number' && Number.isFinite(v) ? clamp(v, lo, hi) : d;
  const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
  return {
    volume: num(r.volume, DEFAULT_PREFS.volume, 0, 1),
    muted: bool(r.muted, DEFAULT_PREFS.muted),
    speed: num(r.speed, DEFAULT_PREFS.speed, SPEED_MIN, SPEED_MAX),
    preservePitch: bool(r.preservePitch, DEFAULT_PREFS.preservePitch),
    quality: r.quality === 'hd' || r.quality === 'standard' ? r.quality : 'auto',
    fit: r.fit === 'fill' ? 'fill' : 'fit',
    brightness: num(r.brightness, 1, 0.5, 1.5),
    contrast: num(r.contrast, 1, 0.5, 1.5),
    ambient: bool(r.ambient, DEFAULT_PREFS.ambient),
    alwaysShowControls: bool(r.alwaysShowControls, false),
    largeControls: bool(r.largeControls, false),
    captionsOn: bool(r.captionsOn, false),
    captionLang:
      typeof r.captionLang === 'string' && /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/.test(r.captionLang)
        ? r.captionLang
        : '',
    captionSize: r.captionSize === 'small' || r.captionSize === 'large' ? r.captionSize : 'medium',
    captionBackground: bool(r.captionBackground, true),
  };
}

function readInitial(): PlayerPrefs {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? sanitize(JSON.parse(raw)) : { ...DEFAULT_PREFS };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

function persist(prefs: PlayerPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable — the choice still holds for this visit
  }
}

interface PlayerPrefsState {
  prefs: PlayerPrefs;
  set: (patch: Partial<PlayerPrefs>) => void;
  reset: () => void;
}

export const usePlayerPrefs = create<PlayerPrefsState>((set) => ({
  prefs: readInitial(),
  set: (patch) =>
    set((s) => {
      const prefs = sanitize({ ...s.prefs, ...patch });
      persist(prefs);
      return { prefs };
    }),
  reset: () =>
    set(() => {
      persist(DEFAULT_PREFS);
      return { prefs: { ...DEFAULT_PREFS } };
    }),
}));

/** Which file to play: HD only when the viewer chose it or "Auto" found room for it. */
export function pickSource(
  video: { url: string; lightUrl: string },
  quality: Quality,
  autoWantsHd: boolean,
): 'full' | 'light' {
  if (video.url === video.lightUrl) return 'full';
  if (quality === 'hd') return 'full';
  if (quality === 'standard') return 'light';
  return autoWantsHd ? 'full' : 'light';
}

/**
 * "Auto": HD when the player is shown larger than the light copy's height on a screen that
 * can use it, and the browser doesn't report a slow or data-saving connection.
 */
export function autoWantsHd(displayHeightPx: number, lightHeight: number): boolean {
  const conn = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (conn?.saveData || (conn?.effectiveType && /(^|-)(2g|3g)$/.test(conn.effectiveType))) {
    return false;
  }
  return displayHeightPx * (window.devicePixelRatio || 1) > lightHeight * 1.1;
}

/** The light copy fits 1280×720 (long × short side) — the device makes it (videoPrepare). */
export function renditions(v: { width: number; height: number }) {
  const long = Math.max(v.width, v.height);
  const short = Math.min(v.width, v.height);
  const scale = Math.min(1, 1280 / long, 720 / short);
  return {
    lightHeight: Math.round(v.height * scale),
    hdLabel: `${short}p`,
    standardLabel: `${Math.round(short * scale)}p`,
  };
}
